// ============================================================================
// ADMIN DATING - DASHBOARD PRO
// Vue d'ensemble complète et attrayante du module Dating
// ============================================================================

import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Heart,
  Users,
  MessageCircle,
  Crown,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  DollarSign,
  Eye,
  Activity,
  Calendar,
  RefreshCw,
  Sparkles,
  Zap,
  Shield,
  ShieldCheck,
  Star,
  Clock,
  MapPin,
  Target,
  PieChart,
  ThumbsUp,
  Ban,
  CheckCircle,
  Percent,
  Bell,
  ChevronRight
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useTenantContext } from '../../../context/TenantContext';
import { useToast } from '../../../components/ui/use-toast';
import { applyDomainFilterToQuery } from '../../../hooks/useDomainQuery';
import { format, subDays, startOfDay, endOfDay, differenceInYears, subMonths, isToday, isYesterday } from 'date-fns';
import { fr } from 'date-fns/locale';

// Formatage simple des montants en FCFA pour le dashboard
const formatAmount = (amount: number): string => {
  return `${amount.toLocaleString('fr-FR')} FCFA`;
};

// Types pour les statistiques enrichies
interface DatingStats {
  profiles: {
    total: number;
    active: number;
    verified: number;
    banned: number;
    online: number;
    boosted: number;
    newToday: number;
    newThisWeek: number;
    newThisMonth: number;
    growthRate: number;
    byGender: { male: number; female: number; other: number };
    avgAge: number;
  };
  subscriptions: {
    totalVip: number;
    activeVip: number;
    revenue: number;
    revenueThisMonth: number;
    revenueLastMonth: number;
    revenueGrowth: number;
    conversionRate: number;
    avgOrderValue: number;
  };
  interactions: {
    totalLikes: number;
    totalMatches: number;
    totalSuperLikes: number;
    matchRate: number;
    likesToday: number;
    matchesToday: number;
    avgLikesPerUser: number;
  };
  messages: {
    total: number;
    today: number;
    yesterday: number;
    activeConversations: number;
    avgMessagesPerConversation: number;
  };
  reports: {
    pending: number;
    reviewed: number;
    resolved: number;
    total: number;
  };
  engagement: {
    dailyActiveUsers: number;
    weeklyActiveUsers: number;
    monthlyActiveUsers: number;
    retentionRate: number;
  };
}

// Types pour l'activité récente
interface RecentActivity {
  id: string;
  type: 'new_profile' | 'new_match' | 'new_vip' | 'report' | 'message';
  title: string;
  description: string;
  timestamp: string;
  avatar?: string;
  icon: React.ReactNode;
  color: string;
}

// Types pour les profils récents
interface RecentProfile {
  id: string;
  display_name: string;
  avatar_url?: string;
  gender: string;
  city?: string;
  is_vip: boolean;
  is_verified: boolean;
  is_online: boolean;
  created_at: string;
  total_matches: number;
  birth_date?: string;
}

// Calcul de l'âge
const calculateAge = (birthDate: string): number => {
  if (!birthDate) return 0;
  return differenceInYears(new Date(), new Date(birthDate));
};

// Composant pour les barres de progression
const ProgressBar = ({ value, max, color = 'pink' }: { value: number; max: number; color?: string }) => {
  const percentage = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const colorClasses: Record<string, string> = {
    pink: 'from-pink-500 to-rose-500',
    blue: 'from-blue-500 to-cyan-500',
    green: 'from-green-500 to-emerald-500',
    yellow: 'from-yellow-500 to-amber-500',
    purple: 'from-purple-500 to-violet-500',
    red: 'from-red-500 to-orange-500',
  };
  return (
    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
      <div 
        className={`h-full bg-gradient-to-r ${colorClasses[color] || colorClasses.pink} transition-all duration-500`}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
};

// Composant pour les mini-graphiques (sparkline simple)
const MiniChart = ({ data, color = 'pink' }: { data: number[]; color?: string }) => {
  const max = Math.max(...data, 1);
  const colorClasses: Record<string, string> = {
    pink: 'bg-pink-500',
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
  };
  return (
    <div className="flex items-end gap-0.5 h-8">
      {data.map((value, index) => (
        <div
          key={index}
          className={`w-1.5 ${colorClasses[color] || colorClasses.pink} rounded-t opacity-70 hover:opacity-100 transition-opacity`}
          style={{ height: `${(value / max) * 100}%`, minHeight: '2px' }}
        />
      ))}
    </div>
  );
};

export default function DatingDashboard() {
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  const [stats, setStats] = useState<DatingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [recentProfiles, setRecentProfiles] = useState<RecentProfile[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [topCities, setTopCities] = useState<{ city: string; count: number }[]>([]);
  const [weeklyData, setWeeklyData] = useState<number[]>([0, 0, 0, 0, 0, 0, 0]);

  const fetchStats = async () => {
    if (!tenant?.id) return;

    try {
      const today = new Date();
      const startOfToday = startOfDay(today).toISOString();
      const endOfToday = endOfDay(today).toISOString();
      const yesterday = subDays(today, 1);
      const startOfYesterday = startOfDay(yesterday).toISOString();
      const endOfYesterday = endOfDay(yesterday).toISOString();
      const weekAgo = subDays(today, 7).toISOString();
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString();
      const lastMonthStart = subMonths(new Date(today.getFullYear(), today.getMonth(), 1), 1).toISOString();
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0).toISOString();

      // Récupérer tous les profils pour les stats détaillées
      let profilesQuery = supabase
        .from('dating_profiles')
        .select('id, gender, birth_date, city, is_active, is_verified, is_banned, is_online, is_boosted, is_vip, created_at, last_active_at, total_matches, display_name, avatar_url');
      profilesQuery = applyDomainFilterToQuery(profilesQuery);
      const { data: allProfiles } = await profilesQuery;

      const profiles = allProfiles || [];
      const totalProfiles = profiles.length;
      const activeProfiles = profiles.filter(p => p.is_active && !p.is_banned).length;
      const verifiedProfiles = profiles.filter(p => p.is_verified).length;
      const bannedProfiles = profiles.filter(p => p.is_banned).length;
      const onlineProfiles = profiles.filter(p => p.is_online).length;
      const boostedProfiles = profiles.filter(p => p.is_boosted).length;
      const newToday = profiles.filter(p => new Date(p.created_at) >= new Date(startOfToday)).length;
      const newThisWeek = profiles.filter(p => new Date(p.created_at) >= new Date(weekAgo)).length;
      const newThisMonth = profiles.filter(p => new Date(p.created_at) >= new Date(monthStart)).length;
      const newLastMonth = profiles.filter(p => {
        const d = new Date(p.created_at);
        return d >= new Date(lastMonthStart) && d <= new Date(lastMonthEnd);
      }).length;
      const growthRate = newLastMonth > 0 ? ((newThisMonth - newLastMonth) / newLastMonth * 100) : 0;

      // Stats par genre
      const maleCount = profiles.filter(p => p.gender === 'male').length;
      const femaleCount = profiles.filter(p => p.gender === 'female').length;
      const otherCount = profiles.filter(p => p.gender !== 'male' && p.gender !== 'female').length;

      // Âge moyen
      const ages = profiles.filter(p => p.birth_date).map(p => calculateAge(p.birth_date));
      const avgAge = ages.length > 0 ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length) : 0;

      // Top villes
      const cityCounts: Record<string, number> = {};
      profiles.forEach(p => {
        if (p.city) {
          cityCounts[p.city] = (cityCounts[p.city] || 0) + 1;
        }
      });
      const sortedCities = Object.entries(cityCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([city, count]) => ({ city, count }));
      setTopCities(sortedCities);

      // Profils récents
      const recent = profiles
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 6)
        .map(p => ({
          id: p.id,
          display_name: p.display_name,
          avatar_url: p.avatar_url,
          gender: p.gender,
          city: p.city,
          is_vip: p.is_vip,
          is_verified: p.is_verified,
          is_online: p.is_online,
          created_at: p.created_at,
          total_matches: p.total_matches || 0,
          birth_date: p.birth_date
        }));
      setRecentProfiles(recent);

      // Données hebdomadaires pour le graphique
      const weeklyStats = [];
      for (let i = 6; i >= 0; i--) {
        const day = subDays(today, i);
        const dayStart = startOfDay(day);
        const dayEnd = endOfDay(day);
        const count = profiles.filter(p => {
          const d = new Date(p.created_at);
          return d >= dayStart && d <= dayEnd;
        }).length;
        weeklyStats.push(count);
      }
      setWeeklyData(weeklyStats);

      // Abonnements VIP
      let vipQuery = supabase.from('dating_subscriptions').select('*', { count: 'exact', head: true });
      vipQuery = applyDomainFilterToQuery(vipQuery);
      const { count: totalVip } = await vipQuery;

      let activeVipQuery = supabase.from('dating_subscriptions').select('*', { count: 'exact', head: true }).eq('status', 'active');
      activeVipQuery = applyDomainFilterToQuery(activeVipQuery);
      const { count: activeVip } = await activeVipQuery;

      // Revenus
      let revenueQuery = supabase.from('dating_orders').select('amount, paid_at').eq('status', 'paid');
      revenueQuery = applyDomainFilterToQuery(revenueQuery);
      const { data: revenueData } = await revenueQuery;

      const totalRevenue = revenueData?.reduce((sum, o) => sum + (o.amount || 0), 0) || 0;
      const revenueThisMonth = revenueData?.filter(o => o.paid_at && new Date(o.paid_at) >= new Date(monthStart))
        .reduce((sum, o) => sum + (o.amount || 0), 0) || 0;
      const revenueLastMonth = revenueData?.filter(o => {
        if (!o.paid_at) return false;
        const d = new Date(o.paid_at);
        return d >= new Date(lastMonthStart) && d <= new Date(lastMonthEnd);
      }).reduce((sum, o) => sum + (o.amount || 0), 0) || 0;
      const revenueGrowth = revenueLastMonth > 0 ? ((revenueThisMonth - revenueLastMonth) / revenueLastMonth * 100) : 0;
      const paidOrdersCount = revenueData?.length || 0;
      const avgOrderValue = paidOrdersCount > 0 ? totalRevenue / paidOrdersCount : 0;
      const conversionRate = totalProfiles > 0 ? ((activeVip || 0) / totalProfiles * 100) : 0;

      // Interactions
      let likesQuery = supabase.from('dating_interactions').select('*', { count: 'exact', head: true }).eq('interaction_type', 'like');
      likesQuery = applyDomainFilterToQuery(likesQuery);
      const { count: totalLikes } = await likesQuery;

      let superLikesQuery = supabase.from('dating_interactions').select('*', { count: 'exact', head: true }).eq('interaction_type', 'super_like');
      superLikesQuery = applyDomainFilterToQuery(superLikesQuery);
      const { count: totalSuperLikes } = await superLikesQuery;

      let matchesQuery = supabase.from('dating_matches').select('*', { count: 'exact', head: true });
      matchesQuery = applyDomainFilterToQuery(matchesQuery);
      const { count: totalMatches } = await matchesQuery;

      let likesTodayQuery = supabase.from('dating_interactions').select('*', { count: 'exact', head: true })
        .eq('interaction_type', 'like').gte('created_at', startOfToday);
      likesTodayQuery = applyDomainFilterToQuery(likesTodayQuery);
      const { count: likesToday } = await likesTodayQuery;

      let matchesTodayQuery = supabase.from('dating_matches').select('*', { count: 'exact', head: true }).gte('matched_at', startOfToday);
      matchesTodayQuery = applyDomainFilterToQuery(matchesTodayQuery);
      const { count: matchesToday } = await matchesTodayQuery;

      const matchRate = totalLikes && totalLikes > 0 ? ((totalMatches || 0) / totalLikes * 100) : 0;
      const avgLikesPerUser = activeProfiles > 0 ? (totalLikes || 0) / activeProfiles : 0;

      // Messages
      let messagesQuery = supabase.from('dating_messages').select('*', { count: 'exact', head: true });
      messagesQuery = applyDomainFilterToQuery(messagesQuery);
      const { count: totalMessages } = await messagesQuery;

      let messagesTodayQuery = supabase.from('dating_messages').select('*', { count: 'exact', head: true }).gte('sent_at', startOfToday);
      messagesTodayQuery = applyDomainFilterToQuery(messagesTodayQuery);
      const { count: messagesToday } = await messagesTodayQuery;

      let messagesYesterdayQuery = supabase.from('dating_messages').select('*', { count: 'exact', head: true })
        .gte('sent_at', startOfYesterday).lte('sent_at', endOfYesterday);
      messagesYesterdayQuery = applyDomainFilterToQuery(messagesYesterdayQuery);
      const { count: messagesYesterday } = await messagesYesterdayQuery;

      let conversationsQuery = supabase.from('dating_conversations').select('*', { count: 'exact', head: true }).eq('is_active', true);
      conversationsQuery = applyDomainFilterToQuery(conversationsQuery);
      const { count: activeConversations } = await conversationsQuery;

      const avgMessagesPerConversation = (activeConversations || 0) > 0 ? (totalMessages || 0) / (activeConversations || 1) : 0;

      // Signalements
      let pendingReportsQuery = supabase.from('dating_reports').select('*', { count: 'exact', head: true }).eq('status', 'pending');
      pendingReportsQuery = applyDomainFilterToQuery(pendingReportsQuery);
      const { count: pendingReports } = await pendingReportsQuery;

      let reviewedReportsQuery = supabase.from('dating_reports').select('*', { count: 'exact', head: true }).eq('status', 'reviewed');
      reviewedReportsQuery = applyDomainFilterToQuery(reviewedReportsQuery);
      const { count: reviewedReports } = await reviewedReportsQuery;

      let resolvedReportsQuery = supabase.from('dating_reports').select('*', { count: 'exact', head: true }).eq('status', 'resolved');
      resolvedReportsQuery = applyDomainFilterToQuery(resolvedReportsQuery);
      const { count: resolvedReports } = await resolvedReportsQuery;

      let totalReportsQuery = supabase.from('dating_reports').select('*', { count: 'exact', head: true });
      totalReportsQuery = applyDomainFilterToQuery(totalReportsQuery);
      const { count: totalReports } = await totalReportsQuery;

      // Engagement (utilisateurs actifs)
      const dailyActiveUsers = profiles.filter(p => {
        if (!p.last_active_at) return false;
        return new Date(p.last_active_at) >= new Date(startOfToday);
      }).length;
      const weeklyActiveUsers = profiles.filter(p => {
        if (!p.last_active_at) return false;
        return new Date(p.last_active_at) >= new Date(weekAgo);
      }).length;
      const monthlyActiveUsers = profiles.filter(p => {
        if (!p.last_active_at) return false;
        return new Date(p.last_active_at) >= new Date(monthStart);
      }).length;
      const retentionRate = totalProfiles > 0 ? (weeklyActiveUsers / totalProfiles * 100) : 0;

      // Activité récente
      const activities: RecentActivity[] = [];
      
      // Nouveaux profils
      recent.slice(0, 3).forEach(p => {
        activities.push({
          id: `profile-${p.id}`,
          type: 'new_profile',
          title: 'Nouveau profil',
          description: p.display_name,
          timestamp: p.created_at,
          avatar: p.avatar_url,
          icon: <Users className="w-4 h-4" />,
          color: 'pink'
        });
      });

      // Récupérer les derniers matches
      let recentMatchesQuery = supabase.from('dating_matches')
        .select('id, matched_at, profile1:dating_profiles!dating_matches_profile1_id_fkey(display_name), profile2:dating_profiles!dating_matches_profile2_id_fkey(display_name)')
        .order('matched_at', { ascending: false })
        .limit(3);
      recentMatchesQuery = applyDomainFilterToQuery(recentMatchesQuery);
      const { data: recentMatches } = await recentMatchesQuery;

      recentMatches?.forEach((m: any) => {
        activities.push({
          id: `match-${m.id}`,
          type: 'new_match',
          title: 'Nouveau match',
          description: `${m.profile1?.display_name || 'Profil'} ❤️ ${m.profile2?.display_name || 'Profil'}`,
          timestamp: m.matched_at,
          icon: <Heart className="w-4 h-4" />,
          color: 'red'
        });
      });

      // Trier par date
      activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setRecentActivity(activities.slice(0, 8));

      setStats({
        profiles: {
          total: totalProfiles,
          active: activeProfiles,
          verified: verifiedProfiles,
          banned: bannedProfiles,
          online: onlineProfiles,
          boosted: boostedProfiles,
          newToday,
          newThisWeek,
          newThisMonth,
          growthRate: Math.round(growthRate * 10) / 10,
          byGender: { male: maleCount, female: femaleCount, other: otherCount },
          avgAge
        },
        subscriptions: {
          totalVip: totalVip || 0,
          activeVip: activeVip || 0,
          revenue: totalRevenue,
          revenueThisMonth,
          revenueLastMonth,
          revenueGrowth: Math.round(revenueGrowth * 10) / 10,
          conversionRate: Math.round(conversionRate * 10) / 10,
          avgOrderValue: Math.round(avgOrderValue)
        },
        interactions: {
          totalLikes: totalLikes || 0,
          totalMatches: totalMatches || 0,
          totalSuperLikes: totalSuperLikes || 0,
          matchRate: Math.round(matchRate * 10) / 10,
          likesToday: likesToday || 0,
          matchesToday: matchesToday || 0,
          avgLikesPerUser: Math.round(avgLikesPerUser * 10) / 10
        },
        messages: {
          total: totalMessages || 0,
          today: messagesToday || 0,
          yesterday: messagesYesterday || 0,
          activeConversations: activeConversations || 0,
          avgMessagesPerConversation: Math.round(avgMessagesPerConversation)
        },
        reports: {
          pending: pendingReports || 0,
          reviewed: reviewedReports || 0,
          resolved: resolvedReports || 0,
          total: totalReports || 0
        },
        engagement: {
          dailyActiveUsers,
          weeklyActiveUsers,
          monthlyActiveUsers,
          retentionRate: Math.round(retentionRate * 10) / 10
        }
      });
    } catch (error) {
      console.error('Erreur chargement stats Dating:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les statistiques',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [tenant]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStats();
  };

  // Formatage du temps relatif
  const formatRelativeTime = (timestamp: string) => {
    const date = new Date(timestamp);
    if (isToday(date)) {
      return `Aujourd'hui à ${format(date, 'HH:mm', { locale: fr })}`;
    }
    if (isYesterday(date)) {
      return `Hier à ${format(date, 'HH:mm', { locale: fr })}`;
    }
    return format(date, 'dd MMM à HH:mm', { locale: fr });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-96 gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-4 border-pink-500/20 border-t-pink-500 animate-spin" />
            <Heart className="w-6 h-6 text-pink-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-gray-400 text-lg">Chargement du tableau de bord Dating...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* ============================================================================ */}
        {/* HEADER PRINCIPAL - Design moderne avec gradient et animations */}
        {/* ============================================================================ */}
        <Card className="relative overflow-hidden bg-gradient-to-r from-pink-600 via-rose-600 to-purple-700 border-none shadow-2xl">
          {/* Effet de particules en arrière-plan */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-10 right-20 w-40 h-40 bg-pink-300/10 rounded-full blur-3xl animate-pulse delay-1000" />
            <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-purple-300/10 rounded-full blur-2xl animate-pulse delay-500" />
          </div>
          
          <CardContent className="relative py-8 px-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="flex items-center gap-5">
                <div className="p-4 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 shadow-lg">
                  <Heart className="w-10 h-10 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-4xl font-bold text-white tracking-tight">
                      Module Dating
                    </h1>
                    {stats && (
                      <>
                        <Badge className="bg-white/20 text-white border-white/30 text-sm px-3 py-1">
                          <Users className="w-3.5 h-3.5 mr-1.5" />
                          {stats.profiles.total} profils
                        </Badge>
                        {stats.profiles.online > 0 && (
                          <Badge className="bg-green-500/30 text-green-100 border-green-400/30 text-sm px-3 py-1 animate-pulse">
                            <span className="w-2 h-2 bg-green-400 rounded-full mr-1.5 inline-block" />
                            {stats.profiles.online} en ligne
                          </Badge>
                        )}
                      </>
                    )}
                  </div>
                  <p className="text-pink-100 mt-2 text-base max-w-xl">
                    Tableau de bord complet pour gérer votre plateforme de rencontres : profils, matches, abonnements et sécurité.
                  </p>
                  <div className="flex items-center gap-4 mt-4 text-sm text-pink-100/90">
                    <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full">
                      <Calendar className="w-4 h-4" />
                      {format(new Date(), 'EEEE d MMMM yyyy', { locale: fr })}
                    </div>
                    <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full">
                      <Clock className="w-4 h-4" />
                      {format(new Date(), 'HH:mm', { locale: fr })}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Button
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="border-white/30 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  Actualiser
                </Button>
                <Link to="/admin/dating/profiles">
                  <Button className="bg-white text-pink-600 hover:bg-pink-50 shadow-lg w-full sm:w-auto">
                    <Users className="w-4 h-4 mr-2" />
                    Gérer les profils
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ============================================================================ */}
        {/* ALERTES - Signalements en attente */}
        {/* ============================================================================ */}
        {stats && stats.reports.pending > 0 && (
          <Card className="border-orange-500/50 bg-gradient-to-r from-orange-500/15 to-red-500/10 backdrop-blur-sm">
            <CardContent className="py-4 px-6">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-orange-500/20 border border-orange-500/30">
                    <AlertTriangle className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-orange-100 text-lg">
                      {stats.reports.pending} signalement{stats.reports.pending > 1 ? 's' : ''} en attente
                    </p>
                    <p className="text-sm text-orange-200/80">
                      Traitez rapidement les profils signalés pour maintenir la qualité de la communauté.
                    </p>
                  </div>
                </div>
                <Link to="/admin/dating/reports">
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white shadow-lg">
                    <Eye className="w-4 h-4 mr-2" />
                    Traiter maintenant
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ============================================================================ */}
        {/* STATISTIQUES PRINCIPALES - 5 cartes avec design moderne */}
        {/* ============================================================================ */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Profils totaux */}
            <Card className="bg-gradient-to-br from-pink-500/20 via-pink-500/10 to-rose-500/5 border-pink-500/30 hover:border-pink-500/50 transition-all hover:shadow-lg hover:shadow-pink-500/10">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-pink-200/80">Profils totaux</p>
                    <p className="text-3xl font-bold text-white mt-1">{stats.profiles.total}</p>
                    <div className="flex items-center gap-2 mt-3">
                      {stats.profiles.growthRate >= 0 ? (
                        <Badge className="bg-green-500/20 text-green-300 border-green-500/30 text-xs">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          +{stats.profiles.growthRate}%
                        </Badge>
                      ) : (
                        <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-xs">
                          <TrendingDown className="w-3 h-3 mr-1" />
                          {stats.profiles.growthRate}%
                        </Badge>
                      )}
                      <span className="text-xs text-pink-200/60">vs mois dernier</span>
                    </div>
                  </div>
                  <div className="p-2.5 bg-pink-500/20 rounded-xl">
                    <Users className="w-6 h-6 text-pink-300" />
                  </div>
                </div>
                <div className="mt-4">
                  <MiniChart data={weeklyData} color="pink" />
                  <p className="text-xs text-pink-200/60 mt-1">7 derniers jours</p>
                </div>
              </CardContent>
            </Card>

            {/* Matches */}
            <Card className="bg-gradient-to-br from-red-500/20 via-red-500/10 to-orange-500/5 border-red-500/30 hover:border-red-500/50 transition-all hover:shadow-lg hover:shadow-red-500/10">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-red-200/80">Matches</p>
                    <p className="text-3xl font-bold text-white mt-1">{stats.interactions.totalMatches}</p>
                    <div className="flex items-center gap-2 mt-3">
                      <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-xs">
                        <Heart className="w-3 h-3 mr-1" />
                        +{stats.interactions.matchesToday} aujourd'hui
                      </Badge>
                    </div>
                  </div>
                  <div className="p-2.5 bg-red-500/20 rounded-xl">
                    <Heart className="w-6 h-6 text-red-300" />
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-red-200/70">Taux de match</span>
                    <span className="text-white font-medium">{stats.interactions.matchRate}%</span>
                  </div>
                  <ProgressBar value={stats.interactions.matchRate} max={100} color="red" />
                </div>
              </CardContent>
            </Card>

            {/* VIP actifs */}
            <Card className="bg-gradient-to-br from-yellow-500/20 via-yellow-500/10 to-amber-500/5 border-yellow-500/30 hover:border-yellow-500/50 transition-all hover:shadow-lg hover:shadow-yellow-500/10">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-yellow-200/80">Abonnés VIP</p>
                    <p className="text-3xl font-bold text-white mt-1">{stats.subscriptions.activeVip}</p>
                    <div className="flex items-center gap-2 mt-3">
                      <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30 text-xs">
                        <Percent className="w-3 h-3 mr-1" />
                        {stats.subscriptions.conversionRate}% conversion
                      </Badge>
                    </div>
                  </div>
                  <div className="p-2.5 bg-yellow-500/20 rounded-xl">
                    <Crown className="w-6 h-6 text-yellow-300" />
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-yellow-200/70">Sur {stats.subscriptions.totalVip} total</span>
                    <span className="text-white font-medium">{stats.subscriptions.activeVip}/{stats.subscriptions.totalVip}</span>
                  </div>
                  <ProgressBar value={stats.subscriptions.activeVip} max={stats.subscriptions.totalVip || 1} color="yellow" />
                </div>
              </CardContent>
            </Card>

            {/* Revenus */}
            <Card className="bg-gradient-to-br from-emerald-500/20 via-emerald-500/10 to-teal-500/5 border-emerald-500/30 hover:border-emerald-500/50 transition-all hover:shadow-lg hover:shadow-emerald-500/10">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-emerald-200/80">Revenus ce mois</p>
                    <p className="text-2xl font-bold text-white mt-1">{formatAmount(stats.subscriptions.revenueThisMonth)}</p>
                    <div className="flex items-center gap-2 mt-3">
                      {stats.subscriptions.revenueGrowth >= 0 ? (
                        <Badge className="bg-green-500/20 text-green-300 border-green-500/30 text-xs">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          +{stats.subscriptions.revenueGrowth}%
                        </Badge>
                      ) : (
                        <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-xs">
                          <TrendingDown className="w-3 h-3 mr-1" />
                          {stats.subscriptions.revenueGrowth}%
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="p-2.5 bg-emerald-500/20 rounded-xl">
                    <DollarSign className="w-6 h-6 text-emerald-300" />
                  </div>
                </div>
                <div className="mt-4 text-xs text-emerald-200/70">
                  <div className="flex justify-between">
                    <span>Total cumulé</span>
                    <span className="text-white">{formatAmount(stats.subscriptions.revenue)}</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span>Panier moyen</span>
                    <span className="text-white">{formatAmount(stats.subscriptions.avgOrderValue)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Messages */}
            <Card className="bg-gradient-to-br from-blue-500/20 via-blue-500/10 to-cyan-500/5 border-blue-500/30 hover:border-blue-500/50 transition-all hover:shadow-lg hover:shadow-blue-500/10">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-200/80">Messages</p>
                    <p className="text-3xl font-bold text-white mt-1">{stats.messages.total.toLocaleString()}</p>
                    <div className="flex items-center gap-2 mt-3">
                      <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30 text-xs">
                        <MessageCircle className="w-3 h-3 mr-1" />
                        +{stats.messages.today} aujourd'hui
                      </Badge>
                    </div>
                  </div>
                  <div className="p-2.5 bg-blue-500/20 rounded-xl">
                    <MessageCircle className="w-6 h-6 text-blue-300" />
                  </div>
                </div>
                <div className="mt-4 text-xs text-blue-200/70">
                  <div className="flex justify-between">
                    <span>Conversations actives</span>
                    <span className="text-white">{stats.messages.activeConversations}</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span>Hier</span>
                    <span className="text-white">{stats.messages.yesterday} messages</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ============================================================================ */}
        {/* SECTION DÉTAILLÉE - 3 colonnes */}
        {/* ============================================================================ */}
        {stats && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            
            {/* ============================================================================ */}
            {/* COLONNE 1 : Engagement & Activité */}
            {/* ============================================================================ */}
            <Card className="bg-white/5 backdrop-blur-sm border border-white/10">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2 text-white">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <Activity className="w-5 h-5 text-blue-400" />
                  </div>
                  Engagement & Activité
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Utilisateurs actifs */}
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-300">Utilisateurs actifs</span>
                    <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                      {stats.engagement.retentionRate}% rétention
                    </Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-2 bg-white/5 rounded-lg">
                      <p className="text-xl font-bold text-white">{stats.engagement.dailyActiveUsers}</p>
                      <p className="text-xs text-gray-400">Jour</p>
                    </div>
                    <div className="text-center p-2 bg-white/5 rounded-lg">
                      <p className="text-xl font-bold text-white">{stats.engagement.weeklyActiveUsers}</p>
                      <p className="text-xs text-gray-400">Semaine</p>
                    </div>
                    <div className="text-center p-2 bg-white/5 rounded-lg">
                      <p className="text-xl font-bold text-white">{stats.engagement.monthlyActiveUsers}</p>
                      <p className="text-xs text-gray-400">Mois</p>
                    </div>
                  </div>
                </div>

                {/* Statistiques d'interaction */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <ThumbsUp className="w-5 h-5 text-pink-400" />
                      <span className="text-gray-300">Likes aujourd'hui</span>
                    </div>
                    <span className="font-semibold text-white">{stats.interactions.likesToday}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Star className="w-5 h-5 text-yellow-400" />
                      <span className="text-gray-300">Super Likes total</span>
                    </div>
                    <span className="font-semibold text-white">{stats.interactions.totalSuperLikes}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Target className="w-5 h-5 text-blue-400" />
                      <span className="text-gray-300">Likes/utilisateur</span>
                    </div>
                    <span className="font-semibold text-white">{stats.interactions.avgLikesPerUser}</span>
                  </div>
                </div>

                {/* Inscriptions récentes */}
                <div className="pt-3 border-t border-white/10">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-400">Nouvelles inscriptions</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-2 bg-pink-500/10 rounded-lg border border-pink-500/20">
                      <p className="text-lg font-bold text-pink-300">{stats.profiles.newToday}</p>
                      <p className="text-xs text-pink-200/70">Aujourd'hui</p>
                    </div>
                    <div className="p-2 bg-pink-500/10 rounded-lg border border-pink-500/20">
                      <p className="text-lg font-bold text-pink-300">{stats.profiles.newThisWeek}</p>
                      <p className="text-xs text-pink-200/70">Cette semaine</p>
                    </div>
                    <div className="p-2 bg-pink-500/10 rounded-lg border border-pink-500/20">
                      <p className="text-lg font-bold text-pink-300">{stats.profiles.newThisMonth}</p>
                      <p className="text-xs text-pink-200/70">Ce mois</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ============================================================================ */}
            {/* COLONNE 2 : Démographie & Qualité */}
            {/* ============================================================================ */}
            <Card className="bg-white/5 backdrop-blur-sm border border-white/10">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2 text-white">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <PieChart className="w-5 h-5 text-purple-400" />
                  </div>
                  Démographie & Qualité
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Répartition par genre */}
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <p className="text-sm text-gray-300 mb-3">Répartition par genre</p>
                  <div className="space-y-3">
                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-blue-300">👨 Hommes</span>
                        <span className="text-white">{stats.profiles.byGender.male} ({stats.profiles.total > 0 ? Math.round(stats.profiles.byGender.male / stats.profiles.total * 100) : 0}%)</span>
                      </div>
                      <ProgressBar value={stats.profiles.byGender.male} max={stats.profiles.total || 1} color="blue" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-pink-300">👩 Femmes</span>
                        <span className="text-white">{stats.profiles.byGender.female} ({stats.profiles.total > 0 ? Math.round(stats.profiles.byGender.female / stats.profiles.total * 100) : 0}%)</span>
                      </div>
                      <ProgressBar value={stats.profiles.byGender.female} max={stats.profiles.total || 1} color="pink" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-purple-300">🌈 Autres</span>
                        <span className="text-white">{stats.profiles.byGender.other} ({stats.profiles.total > 0 ? Math.round(stats.profiles.byGender.other / stats.profiles.total * 100) : 0}%)</span>
                      </div>
                      <ProgressBar value={stats.profiles.byGender.other} max={stats.profiles.total || 1} color="purple" />
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                    <span className="text-sm text-gray-400">Âge moyen</span>
                    <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                      {stats.profiles.avgAge} ans
                    </Badge>
                  </div>
                </div>

                {/* Qualité des profils */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-gray-300">Profils actifs</span>
                    </div>
                    <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                      {stats.profiles.active}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="w-5 h-5 text-blue-400" />
                      <span className="text-gray-300">Profils vérifiés</span>
                    </div>
                    <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                      {stats.profiles.verified}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                    <div className="flex items-center gap-3">
                      <Zap className="w-5 h-5 text-yellow-400" />
                      <span className="text-gray-300">Profils boostés</span>
                    </div>
                    <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30">
                      {stats.profiles.boosted}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                    <div className="flex items-center gap-3">
                      <Ban className="w-5 h-5 text-red-400" />
                      <span className="text-gray-300">Profils bannis</span>
                    </div>
                    <Badge className="bg-red-500/20 text-red-300 border-red-500/30">
                      {stats.profiles.banned}
                    </Badge>
                  </div>
                </div>

                {/* Top villes */}
                {topCities.length > 0 && (
                  <div className="pt-3 border-t border-white/10">
                    <p className="text-sm text-gray-400 mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Top villes
                    </p>
                    <div className="space-y-2">
                      {topCities.map((city, index) => (
                        <div key={city.city} className="flex items-center justify-between text-sm">
                          <span className="text-gray-300">
                            {index + 1}. {city.city}
                          </span>
                          <span className="text-white">{city.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ============================================================================ */}
            {/* COLONNE 3 : Actions rapides & Activité récente */}
            {/* ============================================================================ */}
            <div className="space-y-6">
              {/* Actions rapides */}
              <Card className="bg-white/5 backdrop-blur-sm border border-white/10">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg flex items-center gap-2 text-white">
                    <div className="p-2 bg-emerald-500/20 rounded-lg">
                      <Zap className="w-5 h-5 text-emerald-400" />
                    </div>
                    Actions rapides
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Link to="/admin/dating/profiles" className="block">
                    <Button variant="outline" className="w-full justify-between border-white/15 bg-white/5 hover:bg-white/10 h-12">
                      <span className="flex items-center gap-3">
                        <Users className="w-5 h-5 text-pink-400" />
                        <span>Gérer les profils</span>
                      </span>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </Button>
                  </Link>
                  <Link to="/admin/dating/reports" className="block">
                    <Button variant="outline" className="w-full justify-between border-orange-500/30 bg-orange-500/10 hover:bg-orange-500/20 h-12">
                      <span className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-orange-400" />
                        <span>Signalements</span>
                      </span>
                      {stats.reports.pending > 0 && (
                        <Badge className="bg-orange-500 text-white border-none">
                          {stats.reports.pending}
                        </Badge>
                      )}
                    </Button>
                  </Link>
                  <Link to="/admin/dating/plans" className="block">
                    <Button variant="outline" className="w-full justify-between border-yellow-500/30 bg-yellow-500/10 hover:bg-yellow-500/20 h-12">
                      <span className="flex items-center gap-3">
                        <Crown className="w-5 h-5 text-yellow-400" />
                        <span>Plans VIP</span>
                      </span>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </Button>
                  </Link>
                  <Link to="/admin/dating/orders" className="block">
                    <Button variant="outline" className="w-full justify-between border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 h-12">
                      <span className="flex items-center gap-3">
                        <DollarSign className="w-5 h-5 text-emerald-400" />
                        <span>Commandes</span>
                      </span>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Signalements */}
              <Card className="bg-white/5 backdrop-blur-sm border border-white/10">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg flex items-center gap-2 text-white">
                    <div className="p-2 bg-orange-500/20 rounded-lg">
                      <Shield className="w-5 h-5 text-orange-400" />
                    </div>
                    Modération
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20 text-center">
                      <p className="text-2xl font-bold text-yellow-300">{stats.reports.pending}</p>
                      <p className="text-xs text-yellow-200/70">En attente</p>
                    </div>
                    <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20 text-center">
                      <p className="text-2xl font-bold text-blue-300">{stats.reports.reviewed}</p>
                      <p className="text-xs text-blue-200/70">En cours</p>
                    </div>
                    <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20 text-center">
                      <p className="text-2xl font-bold text-green-300">{stats.reports.resolved}</p>
                      <p className="text-xs text-green-200/70">Résolus</p>
                    </div>
                    <div className="p-3 bg-gray-500/10 rounded-lg border border-gray-500/20 text-center">
                      <p className="text-2xl font-bold text-gray-300">{stats.reports.total}</p>
                      <p className="text-xs text-gray-400">Total</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Activité récente */}
              {recentActivity.length > 0 && (
                <Card className="bg-white/5 backdrop-blur-sm border border-white/10">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg flex items-center gap-2 text-white">
                      <div className="p-2 bg-pink-500/20 rounded-lg">
                        <Bell className="w-5 h-5 text-pink-400" />
                      </div>
                      Activité récente
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {recentActivity.slice(0, 5).map((activity) => (
                      <div key={activity.id} className="flex items-center gap-3 p-2 hover:bg-white/5 rounded-lg transition-colors">
                        <div className={`p-2 rounded-lg ${
                          activity.type === 'new_profile' ? 'bg-pink-500/20' :
                          activity.type === 'new_match' ? 'bg-red-500/20' :
                          activity.type === 'new_vip' ? 'bg-yellow-500/20' :
                          'bg-gray-500/20'
                        }`}>
                          {activity.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{activity.description}</p>
                          <p className="text-xs text-gray-400">{formatRelativeTime(activity.timestamp)}</p>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* ============================================================================ */}
        {/* PROFILS RÉCENTS */}
        {/* ============================================================================ */}
        {recentProfiles.length > 0 && (
          <Card className="bg-white/5 backdrop-blur-sm border border-white/10">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2 text-white">
                  <div className="p-2 bg-pink-500/20 rounded-lg">
                    <Sparkles className="w-5 h-5 text-pink-400" />
                  </div>
                  Nouveaux profils
                </CardTitle>
                <Link to="/admin/dating/profiles">
                  <Button variant="ghost" size="sm" className="text-pink-400 hover:text-pink-300 hover:bg-pink-500/10">
                    Voir tous
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {recentProfiles.map((profile) => (
                  <Link 
                    key={profile.id} 
                    to={`/admin/dating/profiles`}
                    className="group"
                  >
                    <div className="relative p-4 bg-white/5 rounded-xl border border-white/10 hover:border-pink-500/30 hover:bg-white/10 transition-all text-center">
                      {/* Avatar */}
                      <div className="relative mx-auto w-16 h-16 mb-3">
                        {profile.avatar_url ? (
                          <img 
                            src={profile.avatar_url} 
                            alt={profile.display_name}
                            className="w-full h-full rounded-full object-cover border-2 border-white/20"
                          />
                        ) : (
                          <div className="w-full h-full rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-white text-xl font-bold">
                            {profile.display_name?.charAt(0) || '?'}
                          </div>
                        )}
                        {/* Indicateur en ligne */}
                        {profile.is_online && (
                          <span className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-gray-900 rounded-full" />
                        )}
                        {/* Badge VIP */}
                        {profile.is_vip && (
                          <span className="absolute -top-1 -right-1 p-1 bg-yellow-500 rounded-full">
                            <Crown className="w-3 h-3 text-white" />
                          </span>
                        )}
                      </div>
                      
                      {/* Nom */}
                      <p className="font-medium text-white truncate text-sm">
                        {profile.display_name}
                      </p>
                      
                      {/* Info */}
                      <div className="flex items-center justify-center gap-1 mt-1 text-xs text-gray-400">
                        {profile.birth_date && (
                          <span>{calculateAge(profile.birth_date)} ans</span>
                        )}
                        {profile.city && (
                          <>
                            <span>•</span>
                            <span className="truncate">{profile.city}</span>
                          </>
                        )}
                      </div>
                      
                      {/* Badges */}
                      <div className="flex items-center justify-center gap-1 mt-2">
                        {profile.is_verified && (
                          <ShieldCheck className="w-4 h-4 text-blue-400" />
                        )}
                        <span className="text-xs text-gray-500">
                          {profile.total_matches} ❤️
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
